import { CardOffer } from '@/components/cards/card-offer';
import { WithCountdown } from '@/components/cards/wrapper/WithCountdown';
import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import CardCarousel from '@/components/ui/carousel/card-carousel';
import { createListCardArray } from '@/helpers/list-page/list-page-helpers';
import { cardIsOffer } from '@/helpers/list-page/type-guards';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import { getBrowserClient } from '@/lib/apolloClient';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';
import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import { getListingPageSlugs } from '@/lib/queries/getEventAndOfferListPageSlug';
import { GET_ACTIVE_OFFERS } from '@/lib/queries/meetingPlacePageOfferQuery';
import {
  useGetActiveOffers,
  useGetPreviewMode,
  useTenantConfig,
  useUrlSubDirectoryDictionary,
} from '@/providers/ConfigProvider';
import React, { useEffect, useState } from 'react';

export interface LivliFamilyCardEventWrapperProps {
  businessUnitCode?: string;
  node_locale: string;
  cardIndex: number;
  dictionary?: ILibraryMeetingPlaceDictionary;
}
const SkeletonLoader = () => (
  <div className="mb-5 aspect-0.65/1 h-auto w-full rounded-xl bg-gradient-to-r from-[rgba(234,227,218,0.2)] via-[rgba(234,227,218,0.5)] to-[rgba(234,227,218,0.2)] bg-[length:200%_100%] animate-wave"></div>
);
const LivliFamilyCardOfferWrapper = ({
  businessUnitCode,
  node_locale,
  dictionary,
}: LivliFamilyCardEventWrapperProps) => {
  const activeOffers = useGetActiveOffers();
  const preview = useGetPreviewMode();
  const [offers, setOffers] = useState<IMeetingPlacePageOfferItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [offerListPageSlug, setOfferListPageSlug] = useState<string | null>(
    null,
  );
  const client = getBrowserClient(preview);
  const urlSubDirectorydictionary = useUrlSubDirectoryDictionary();
  const urlSubdirectory = getUrlSubdirectory(
    urlSubDirectorydictionary,
    node_locale,
    'MeetingPlacePageOffer',
  );
  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX;

  useEffect(() => {
    const offerIds = activeOffers
      ?.filter(
        (offer: any) => offer?.typeOfOffer?.internalName === 'specialOffer',
      )
      ?.map((offer) => offer?.sys?.id);
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await client.query({
          query: GET_ACTIVE_OFFERS,
          variables: {
            ids: offerIds,
            language: node_locale,
            limit: 3,
          },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        const offers =
          (result?.data as any)?.meetingPlacePageOfferCollection?.items || [];
        setOffers(offers);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error('Error fetching offers:', error);
      }
    };

    fetchData();
  }, [activeOffers, node_locale]);

  const listingTag =
    config?.NEXT_PUBLIC_MEETINGPLACE_TAG_CONTENTFUL_ID ||
    config?.MEETINGPLACE_TAG_CONTENTFUL_ID ||
    '';

  useEffect(() => {
    let cancelled = false;
    const fetchSlugs = async () => {
      const client = getBrowserClient(preview);
      const { offerListPageSlug } = await getListingPageSlugs(
        client,
        listingTag,
        node_locale,
        preview,
      );
      if (!cancelled) setOfferListPageSlug(offerListPageSlug);
    };

    fetchSlugs();
    return () => {
      cancelled = true;
    };
  }, [listingTag, node_locale, preview]);

  const offerCardList = createListCardArray(
    offers,
    undefined,
    undefined,
    urlSubdirectory,
    node_locale,
    prefix,
  );
  return (
    <>
      {offerCardList && offerCardList?.length > 0 && (
        <div className="bg-background-primary-light rounded-xl p-5 w-full mb-5">
          <CardCarousel
            items={offerCardList} // OfferCard[]  — needs an `id` field
            isLoading={loading}
            renderSkeleton={() => <SkeletonLoader />}
            renderCard={(card, index) => {
              if (cardIsOffer(card)) {
                return (
                  <WithCountdown key={card.id} card={card}>
                    {({ countdownState }) => (
                      <CardOffer
                        card={card}
                        key={index}
                        cardIndex={index}
                        countdownState={countdownState}
                        variant="home"
                      />
                    )}
                  </WithCountdown>
                );
              }
            }}
            sliderNavClass="mt-5 mb-4"
          />
          <div
            className={`${offerCardList && offerCardList?.length === 1 ? 'mt-4' : ''}`}
          >
            {offerListPageSlug && (
              <LinkWrapper
                data={`/${node_locale}/${offerListPageSlug}`}
                className="flex w-full justify-center text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
              >
                {dictionary?.seeAllOffers}
              </LinkWrapper>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LivliFamilyCardOfferWrapper;

